/**
 * DP28 — can this device install a browser extension at all?
 *
 * The offer used to be identical on every device. Chad's Surface runs DESKTOP
 * Chrome, so it can — the reason he could not find the clipper was that the
 * page offering it was never mounted, not the device. But a phone, an iPad or
 * a Windows machine in S mode genuinely cannot, and those cases have to be told
 * BEFORE the download, not after it fails.
 *
 * No sniffing beyond what is needed, and no guessing: anything this cannot
 * establish returns 'unknown' and the offer stands with the requirement stated.
 */
export const CAN_INSTALL = 'can-install';
export const NO_MOBILE = 'mobile-browser';
export const NO_ENGINE = 'wrong-browser';
export const UNKNOWN = 'unknown';

export function detectExtensionSupport(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return { state: UNKNOWN, headline: '', detail: '' };

  const ua = nav.userAgent || '';
  const uad = nav.userAgentData || null;
  const mobile = uad ? uad.mobile === true : /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const brands = uad?.brands?.map((b) => b.brand).join(' ') || '';
  const isChromium = /Chrome|Chromium|Edg/i.test(brands) || /Chrome|Chromium|Edg/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const iPadOS = /Macintosh/i.test(ua) && (nav.maxTouchPoints || 0) > 1;

  if (mobile || iPadOS) {
    return {
      state: NO_MOBILE,
      headline: 'This device cannot install a browser extension',
      detail: 'Chrome and Safari on phones and tablets have no extension support at all — '
        + 'there is no chrome://extensions to load into. Capture from here with Share to '
        + 'KEY-MADE instead, and install the clipper on the desktop machine.',
    };
  }
  if (isSafari || isFirefox) {
    return {
      state: NO_ENGINE,
      headline: `The clipper needs Chrome or Edge — this is ${isSafari ? 'Safari' : 'Firefox'}`,
      detail: 'The package is a Chrome extension (manifest v3). Open KEY-MADE in desktop '
        + 'Chrome or Edge on this machine and download it there.',
    };
  }
  if (isChromium) {
    return {
      state: CAN_INSTALL,
      headline: 'This browser can install the clipper',
      detail: 'It is not on the Chrome Web Store yet, so it installs unpacked: '
        + 'Developer mode has to be switched on once. Six steps, about a minute.',
    };
  }
  return {
    state: UNKNOWN,
    headline: 'Desktop Chrome or Edge required',
    detail: 'This browser could not be identified. The clipper is a Chrome extension and '
      + 'installs unpacked with Developer mode switched on.',
  };
}

/** Windows S mode cannot be read from the browser. It is asked, not sniffed. */
export const S_MODE_NOTE =
  'On Windows in S mode the sideload is blocked by the operating system, not by the browser, '
  + 'and no web page can detect it. If Load unpacked is greyed out, that is what it is: '
  + 'S mode can be switched off once, permanently, in Windows settings.';
